import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Grid,
  Paper,
  IconButton,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import { AddTestCycleFormData as UpdateProgressFormData, TestCycle, TeamMember, ActionPlanItem } from '../types/test-cycle';
import { Project, CHSRole } from '../types/project';
import { projectService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Legend
} from 'recharts';

interface ProgressChartProps {
  data: { date: string; percentage: number; numerator: number; denominator: number }[];
  target?: number;
  isOnTrack?: boolean;
}

const ProgressChart: React.FC<ProgressChartProps> = ({ data, target, isOnTrack = true }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsLineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
        <YAxis domain={[(dataMin: number) => Math.max(0, Math.floor(dataMin / 5) * 5), 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
        <Tooltip
          formatter={(value: any, name: any) => {
            if (name === 'percentage' && typeof value === 'number') return [`${value.toFixed(1)}%`, 'Achievement'];
            return [value, name];
          }}
          labelFormatter={(label) => `Date: ${label}`}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              const d = payload[0].payload;
              return (
                <Box sx={{ bgcolor: 'white', p: 1.5, border: '1px solid #ccc', borderRadius: 1 }}>
                  <Typography variant="caption" display="block"><strong>{label}</strong></Typography>
                  <Typography variant="caption" display="block" sx={{ color: isOnTrack ? '#4CAF50' : '#FF9800' }}>
                    Achievement: {d.percentage.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" display="block">
                    Numerator: {d.numerator} / Denominator: {d.denominator}
                  </Typography>
                </Box>
              );
            }
            return null;
          }}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="percentage"
          name="Achievement %"
          stroke={isOnTrack ? '#4CAF50' : '#FF9800'}
          strokeWidth={2}
          dot={{ r: 5 }}
          activeDot={{ r: 7 }}
        />
        {target !== undefined && (
          <ReferenceLine
            y={target}
            stroke="#f44336"
            strokeDasharray="5 5"
            label={{ value: `Target: ${target}%`, position: 'right', fontSize: 12, fill: '#f44336' }}
          />
        )}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
};

interface UpdateProgressProps {
  open: boolean;
  projectId?: string; // Optional now as we'll allow selection
  onClose: () => void;
  onSubmit: (testCycle: TestCycle) => void;
  onProjectUpdate?: (project: Project) => void; // Optional callback to update parent component
  requiredDate?: Date | null; // Required date for sequential updates
}

const UpdateProgress: React.FC<UpdateProgressProps> = ({
  open,
  projectId,
  onClose,
  onSubmit,
  onProjectUpdate,
  requiredDate
}) => {
  // Navigation hook
  const navigate = useNavigate();
  
  // State for projects data
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>(projectId || '');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isUpdatingTeam, setIsUpdatingTeam] = useState<boolean>(false);
  const [isUpdatingActionPlan, setIsUpdatingActionPlan] = useState<boolean>(false);

  // State for previous progress updates to show trend
  const [previousUpdates, setPreviousUpdates] = useState<TestCycle[]>([]);
  const [formData, setFormData] = useState<UpdateProgressFormData>({
    numerator: 0,
    denominator: 0,
    testedChange: '',
    comment: '',
    interventionsTaken: '',
    challengesExperienced: '',
    lessonsLearned: '',
    team: [],
    actionPlan: [],
    updateTeam: false,
    sharedInForum: false
  });

  // Set the required date when component opens
  useEffect(() => {
  if (!open) return;   // Clear and early return - cleaner

  fetchProjects();

  if (projectId) {
    setSelectedProjectId(projectId);
    fetchProjectDetails(projectId);
  } else {
    // Optional: reset selection when opening without a specific project
    setSelectedProjectId('');
  }
}, [open, projectId]);

  // Fetch projects for the dropdown
  const fetchProjects = async () => {
    try {
      setIsLoadingProjects(true);
      setError('');
      
      const allProjects = await projectService.getAllProjects();
      
      // Filter out completed projects
      const availableProjects = allProjects.filter(project => 
        project.status?.toLowerCase() !== 'completed' && 
        project.status?.toLowerCase() !== 'archived'
      );
      
      setProjects(availableProjects);
    } catch (err) {
      setError('Failed to load projects. Please try again.');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Fetch project details when a project is selected
  const fetchProjectDetails = async (id: string) => {
    try {
      const project = await projectService.getProjectById(id);
      if (project) {
        setSelectedProject(project);
        
        // If the project has test cycles, fetch them to show trend
        if (project.testCycles && project.testCycles.length > 0) {
          // Convert project's TestCycle type to our TestCycle type as needed
          // We need to handle different TestCycle interfaces between project.ts and test-cycle.ts
          const convertedTestCycles: TestCycle[] = [];
          
          for (const tc of project.testCycles) {
            // Try to extract metrics that might be structured as numerator/denominator
            let num = 0;
            let denom = 0;
            
            if (tc.metrics) {
              num = tc.metrics.numerator || tc.metrics.count || 0;
              denom = tc.metrics.denominator || tc.metrics.total || 0;
            }
            
            convertedTestCycles.push({
              id: tc.id,
              projectId: tc.projectId,
              numerator: num,
              denominator: denom,
              testedChange: '', // No direct mapping
              comment: tc.comments || '',
              interventionsTaken: '',
              challengesExperienced: '',
              lessonsLearned: '',
              team: [],
              updateTeam: false,
              actionPlan: [],
              sharedInForum: false,
              createdAt: tc.startDate || tc.endDate || new Date().toISOString()
            });
          }
          
          setPreviousUpdates(convertedTestCycles);
        }
        
        // Initialize action plan from the project if available
        let initialActionPlan: ActionPlanItem[] = [];
        
        // Try to get action plan from different possible fields
        // Priority: actionPlan (from AddProject) > actionItems > actionPlanItems
        if ((project as any).actionPlan && (project as any).actionPlan.length > 0) {
          initialActionPlan = (project as any).actionPlan.map((item: any, index: number) => {
            return {
              action: item.action || '',
              personResponsible: item.personResponsible || '',
              resources: item.resources || '',
              byWhen: item.byWhen || '',
              place: item.place || '',
              mov: item.mov || '',
              status: item.status || 'Not started'
            };
          });
        } else if (project.actionItems && project.actionItems.length > 0) {
          initialActionPlan = project.actionItems.map((item, index) => {
            return {
              action: item.action || item.text || '',
              personResponsible: item.responsiblePerson || '',
              resources: item.resources || '',
              byWhen: item.byWhen || item.timeline || '',
              place: '', // ActionItem doesn't have place field, so default to empty
              mov: '', // MOV field - default to empty
              status: (item.status as 'Not started' | 'In progress' | 'Completed') || 'Not started'
            };
          });
        } else if (project.actionPlanItems && project.actionPlanItems.length > 0) {
          initialActionPlan = project.actionPlanItems.map((item, index) => {
            return {
              action: item.action || item.text || '',
              personResponsible: item.responsiblePerson || '',
              resources: item.resources || '',
              byWhen: item.byWhen || item.timeline || '',
              place: '', // ActionItem doesn't have place field, so default to empty
              mov: '', // MOV field - default to empty
              status: (item.status as 'Not started' | 'In progress' | 'Completed') || 'Not started'
            };
          });
        }
        
        
        // Store original team members for later use when checkbox is checked
        // Don't populate the team initially - only when user checks "Update Team"
        
        // Update form data with project details (but keep team empty initially)
        setFormData(prev => ({
          ...prev,
          team: [], // Start with empty team
          actionPlan: initialActionPlan
        }));
      }
    } catch (err) {
      setError('Failed to load project details. Please try again.');
    }
  };

  // Handle project selection change
  const handleProjectChange = (event: any) => {
    const id = event.target.value as string;
    setSelectedProjectId(id);
    if (id) {
      fetchProjectDetails(id);
    } else {
      setSelectedProject(null);
      setPreviousUpdates([]);
    }
  };

  // Handle update team checkbox change
  const handleUpdateTeamChange = (checked: boolean) => {
    
    if (checked && selectedProject) {
      let teamMembers: TeamMember[] = [];
      
      // Try to get team members from either participants or chsTeamMembers
      if (selectedProject.participants && selectedProject.participants.length > 0) {
        try {
          teamMembers = selectedProject.participants.map((participant, index) => {
            // Try different possible phone field names
            const phoneNumber = participant.phone || (participant as any).phoneNumber || (participant as any).mobile || '';
            return {
              fullName: participant.fullName || '',
              phone: phoneNumber,
              role: (participant.role as CHSRole) || 'CHA',
              customRole: participant.role === 'Community Member' ? '' : undefined
            };
          });
        } catch (err) {
          console.error('[UpdateProgress] Error processing participants:', err);
        }
      } else if (selectedProject.chsTeamMembers && selectedProject.chsTeamMembers.length > 0) {
        try {
          teamMembers = selectedProject.chsTeamMembers.map((member, index) => {
            // CHSTeamMember uses phoneNumber field
            const phoneNumber = member.phoneNumber || '';
            return {
              fullName: member.fullName || '',
              phone: phoneNumber,
              role: (member.role as CHSRole) || 'CHA',
              customRole: member.role === 'Community Member' ? member.customRole : undefined
            };
          });
        } catch (err) {
          console.error('[UpdateProgress] Error processing CHS team members:', err);
        }
      }

      if (teamMembers.length === 0) {
        console.warn('[UpdateProgress] No team members found in project');
        setError('This project has no team members to load. You can add new team members manually.');
        setFormData(prev => ({
          ...prev,
          updateTeam: true,
          team: []
        }));
        return;
      }
      
      
      setFormData(prev => ({
        ...prev,
        updateTeam: true,
        team: teamMembers
      }));
      
      // Clear any previous error
      setError('');
    } else {
      // Clear team when unchecked
      setFormData(prev => ({
        ...prev,
        updateTeam: false,
        team: []
      }));
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProjectId) {
      setError('Please select a project first.');
      return;
    }
    
    // Always use current date for submission, but add FIFO cycle resolution metadata
    const submissionDate = new Date().toISOString();
    
    
    
    const testCycle: TestCycle = {
      id: crypto.randomUUID(),
      projectId: selectedProjectId,
      ...formData,
      createdAt: submissionDate,
      // Add metadata to indicate this should resolve to the earliest missing cycle
      cycleResolutionDate: requiredDate ? requiredDate.toISOString() : submissionDate
    };
    
  
    
    // Check if project should be marked as completed based on improvement target
    if (selectedProject) {
      const achievedPercentage = (formData.numerator / formData.denominator) * 100;
      const targetPercentage = selectedProject.improvementTo;
      
      
      if (achievedPercentage >= targetPercentage) {
        
        // Update project status to completed
        try {
          const projectId = selectedProject.id || selectedProject._id;
          const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';
          
          const response = await fetch(`${API_URL}/projects/${projectId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ status: 'completed' }),
          });
          
          if (response.ok) {
            const updatedProject = await response.json();
            
            // Update the selected project state and notify parent
            setSelectedProject(updatedProject);
            if (onProjectUpdate) {
              onProjectUpdate(updatedProject);
            }
            
            // Close the dialog first
            onClose();
            
            // Navigate to completed projects page with success message
            setTimeout(() => {
              navigate('/projects/completed', {
                state: {
                  message: `🎉 Project "${selectedProject.projectTitle}" has been completed! Target achieved: ${achievedPercentage.toFixed(1)}%`,
                  completedProject: updatedProject
                }
              });
            }, 500); // Small delay to ensure dialog closes smoothly
            
            return; // Exit early to prevent normal form submission
          }
        } catch (error) {
          console.error('[UpdateProgress] Error updating project status to completed:', error);
        }
      }
    }
    
    onSubmit(testCycle);
    onClose();
  };

  // Function to update team members in the database
  const updateProjectTeamInDatabase = async (updatedTeam: TeamMember[]) => {
    if (!selectedProject?.id && !selectedProject?._id) {
      console.error('[UpdateProgress] No project ID available for team update');
      return;
    }

    // Filter out empty team members to prevent validation errors
    const validTeamMembers = updatedTeam.filter(member => 
      member.fullName && member.fullName.trim() !== ''
    );
    
     

    setIsUpdatingTeam(true);
    try {
      const projectId = selectedProject.id || selectedProject._id;
      
      // Determine which field to update based on the original project structure
      let updateData: Partial<Project>;
      
      if (selectedProject.participants && selectedProject.participants.length > 0) {
        // Project uses participants field - convert TeamMember back to Participant format
        const participants = validTeamMembers.map(member => ({
          fullName: member.fullName,
          email: '', // We don't have email in TeamMember, so use empty string
          phone: member.phone,
          role: member.role
        }));
        updateData = { participants };
      } else if (selectedProject.chsTeamMembers) {
        // Project uses chsTeamMembers field - convert TeamMember to CHSTeamMember format
        const chsTeamMembers = validTeamMembers.map(member => ({
          fullName: member.fullName,
          phoneNumber: member.phone,
          role: member.role,
          customRole: member.customRole
        }));
        updateData = { chsTeamMembers };
      } else {
        // Default to participants if unclear
        const participants = validTeamMembers.map(member => ({
          fullName: member.fullName,
          email: '',
          phone: member.phone,
          role: member.role
        }));
        updateData = { participants };
      }

      // Update the project in the database
      const updatedProject = await projectService.updateProject(projectId, updateData);
      
      // Update the selected project state to reflect the changes
      if (onProjectUpdate) {
        onProjectUpdate(updatedProject);
      }
      
    } catch (error) {
      console.error('[UpdateProgress] Error updating team in database:', error);
      setError('Failed to update team members in database. Changes are only saved locally.');
    } finally {
      setIsUpdatingTeam(false);
    }
  };

  // Function to update action plan in the database
  const updateProjectActionPlanInDatabase = async (updatedActionPlan: ActionPlanItem[]) => {
    if (!selectedProject?.id && !selectedProject?._id) {
      console.error('[UpdateProgress] No project ID available for action plan update');
      return;
    }

    setIsUpdatingActionPlan(true);
    try {
      const projectId = selectedProject.id || selectedProject._id;
      
      // Update the project with the new action plan
      // Convert ActionPlanItem to ActionItem format if needed
      const actionItems = updatedActionPlan.map(item => ({
        action: item.action,
        responsiblePerson: item.personResponsible,
        resources: item.resources,
        byWhen: item.byWhen,
        status: item.status
        // Note: ActionItem doesn't have 'place' field, so we omit it
      }));
      
      const updateData: Partial<Project> = { 
        actionItems: actionItems 
      };
      

      // Update the project in the database
      const updatedProject = await projectService.updateProject(projectId, updateData);
      
      // Update the selected project state to reflect the changes
      if (onProjectUpdate) {
        onProjectUpdate(updatedProject);
      }
      
    } catch (error) {
      console.error('[UpdateProgress] Error updating action plan in database:', error);
      setError('Failed to update action plan in database. Changes are only saved locally.');
    } finally {
      setIsUpdatingActionPlan(false);
    }
  };

  // Function to check if project should be marked as completed
  const shouldMarkProjectAsCompleted = (numerator: number, denominator: number): boolean => {
    // Check if numerator equals denominator (100% achievement)
    const isTargetAchieved = numerator === denominator && denominator > 0;
    
    
    return isTargetAchieved;
  };

  // Function to update project status to archived when shared in forum
  const updateProjectStatusToArchived = async () => {
    
    if (!selectedProject?.id && !selectedProject?._id) {
      console.error('[UpdateProgress] No project ID available for status update to archived');
      setError('No project ID available for status update.');
      return;
    }

    try {
      const projectId = selectedProject.id || selectedProject._id;

      // Update the project in the database - bypass adapter for simple status update
      const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';
      
      const response = await fetch(`${API_URL}/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ status: 'archived' }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[UpdateProgress] API call failed:', response.status, errorText);
        throw new Error(`Failed to update project status: ${response.status} ${response.statusText}`);
      }
      
      const updatedProject = await response.json();
      
      // Update the selected project state to reflect the changes
      setSelectedProject(updatedProject);
      if (onProjectUpdate) {
        onProjectUpdate(updatedProject);
      }
      
      // Clear any previous errors
      setError('');
      
      // Show success message
      
    } catch (error) {
      console.error('[UpdateProgress] Error updating project status to archived:', error);
      setError(`Failed to update project status to archived: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Add New Progress Update</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          {/* FIFO Cycle Resolution Info */}
          {requiredDate && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                backgroundColor: '#e3f2fd',
                border: '1px solid #2196f3'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                🔄 FIFO Cycle Resolution
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Submitting today:</strong> {new Date().toLocaleDateString()}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Will resolve cycle:</strong> {requiredDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                💡 Updates automatically resolve the earliest missing cycle (First In, First Out)
              </Typography>
            </Alert>
          )}
          
          {/* Project Selection - only shown when no projectId is provided */}
          {!projectId && (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <Typography variant="h6" gutterBottom>Select Project</Typography>
                <Select
                  value={selectedProjectId}
                  onChange={handleProjectChange}
                  displayEmpty
                  disabled={isLoadingProjects}
                >
                  <MenuItem value="">
                    <em>{isLoadingProjects ? 'Loading projects...' : 'Select a project'}</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project._id || project.id} value={project._id || project.id}>
                      {project.projectTitle} - {project.status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          
          {/* Project Summary Section - only shown when project is selected */}
          {selectedProject && (
            <Box component={Paper} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Project Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Project Title</Typography>
                  <Typography variant="body2">{selectedProject.projectTitle}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Typography variant="body2">{selectedProject.status}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Responsible Person</Typography>
                  <Typography variant="body2">{selectedProject.responsiblePerson}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Project Start Date:</Typography>
                  <Typography variant="body2">
                    {(() => {
                      const raw = selectedProject?.firstCycleDate || selectedProject?.startDate;
                      if (!raw) return 'Not set';
                      const parts = String(raw).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                      if (parts) return new Date(+parts[3], +parts[2] - 1, +parts[1]).toLocaleDateString();
                      const d = new Date(raw);
                      return isNaN(d.getTime()) ? 'Not set' : d.toLocaleDateString();
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Project End Date:</Typography>
                  <Typography variant="body2">
                    {(() => {
                      const raw = selectedProject?.projectEndDate || selectedProject?.endDate;
                      if (!raw) return 'Not set';
                      const parts = String(raw).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                      if (parts) return new Date(+parts[3], +parts[2] - 1, +parts[1]).toLocaleDateString();
                      const d = new Date(raw);
                      return isNaN(d.getTime()) ? 'Not set' : d.toLocaleDateString();
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Problem Statement</Typography>
                  <Typography variant="body2">{selectedProject.problemStatement}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Project KPI</Typography>
                  <Typography variant="body2">
                    {selectedProject.indicatorDescription}<br />
                    <strong>Numerator:</strong> {selectedProject.indicatorNumerator}<br />
                    <strong>Denominator:</strong> {selectedProject.indicatorDenominator}<br />
                    <strong>Target Improvement:</strong> {selectedProject.improvementFrom}% to {selectedProject.improvementTo}%
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {selectedProject && (
            <Divider sx={{ my: 3 }} />
          )}

          {/* Progress Update Form - only shown when project is selected */}
          {selectedProject && (
            <Box display="flex" flexDirection="column" gap={3}>
              <Typography variant="h6">Provide Progress Update of {selectedProject.projectTitle}</Typography>
              
              <Box display="flex" gap={2}>
                <TextField
                  label="Current Numerator"
                  type="number"
                  value={formData.numerator || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, numerator: e.target.value === '' ? 0 : parseInt(e.target.value) }))}
                  fullWidth
                  required
                  helperText={selectedProject.indicatorNumerator}
                />
                <TextField
                  label="Current Denominator"
                  type="number"
                  value={formData.denominator || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, denominator: e.target.value === '' ? 0 : parseInt(e.target.value) }))}
                  fullWidth
                  required
                  helperText={selectedProject.indicatorDenominator}
                />
              </Box>
              
              {/* Project Completion Indicator */}
              {selectedProject && formData.numerator > 0 && formData.denominator > 0 && (
                (() => {
                  const achievedPercentage = (formData.numerator / formData.denominator) * 100;
                  const targetPercentage = selectedProject.improvementTo;
                  const isTargetAchieved = achievedPercentage >= targetPercentage;
                  
                  if (isTargetAchieved) {
                    return (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          🎉 <strong>Target Achieved!</strong><br />
                          Current: {achievedPercentage.toFixed(1)}% | Target: {targetPercentage}%<br />
                          This project will be automatically marked as <strong>completed</strong> and you'll be redirected to the completed projects page.
                        </Typography>
                      </Alert>
                    );
                  } else {
                    const remainingPercentage = targetPercentage - achievedPercentage;
                    return (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          📊 <strong>Progress Update</strong><br />
                          Current: {achievedPercentage.toFixed(1)}% | Target: {targetPercentage}%<br />
                          Need {remainingPercentage.toFixed(1)}% more to complete the project.
                        </Typography>
                      </Alert>
                    );
                  }
                })()
              )}
              
              <TextField
                label="List Interventions Taken"
                value={formData.interventionsTaken}
                onChange={(e) => setFormData(prev => ({ ...prev, interventionsTaken: e.target.value }))}
                fullWidth
                multiline
                rows={3}
                required
              />
              
              <TextField
                label="List the Challenges Experienced"
                value={formData.challengesExperienced}
                onChange={(e) => setFormData(prev => ({ ...prev, challengesExperienced: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />

              <TextField
                label="List the Support Needed"
                value={formData.supportNeeded || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, supportNeeded: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="List Lessons Learnt"
                value={formData.lessonsLearned}
                onChange={(e) => setFormData(prev => ({ ...prev, lessonsLearned: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
              
              {/* Team Management Section */}
              <Box sx={{ mt: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6">Project Team</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.updateTeam}
                        onChange={(e) => handleUpdateTeamChange(e.target.checked)}
                      />
                    }
                    label="Do you want to update project team?"
                  />
                </Box>
                
                {!formData.updateTeam && selectedProject && (() => {
                  const participantsCount = selectedProject.participants?.length || 0;
                  const chsTeamCount = selectedProject.chsTeamMembers?.length || 0;
                  const totalTeamMembers = participantsCount + chsTeamCount;
                  
                  if (totalTeamMembers > 0) {
                    return (
                      <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                        <Typography variant="body2">
                          Check the box above to load the original project team ({totalTeamMembers} members) and make updates.
                          {participantsCount > 0 && ` (${participantsCount} participants`}
                          {chsTeamCount > 0 && ` ${participantsCount > 0 ? '+ ' : ''}${chsTeamCount} CHS team members`}
                          {(participantsCount > 0 || chsTeamCount > 0) && ')'}
                        </Typography>
                      </Alert>
                    );
                  }
                  return null;
                })()}
                
                
                {formData.updateTeam && (
                  <>
                    {isUpdatingTeam && (
                      <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                        Updating team members in database...
                      </Alert>
                    )}
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                        {formData.team.map((member, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <TextField
                                value={member.fullName}
                                onChange={(e) => {
                                  const updatedTeam = [...formData.team];
                                  updatedTeam[index].fullName = e.target.value;
                                  setFormData(prev => ({ ...prev, team: updatedTeam }));
                                }}
                                onBlur={async () => {
                                  // Update database when user finishes editing the name
                                  if (member.fullName && member.fullName.trim() !== '') {
                                    await updateProjectTeamInDatabase(formData.team);
                                  }
                                }}
                                size="small"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={member.phone || ''}
                                onChange={(e) => {
                                  const updatedTeam = [...formData.team];
                                  updatedTeam[index].phone = e.target.value;
                                  setFormData(prev => ({ ...prev, team: updatedTeam }));
                                }}
                                size="small"
                                fullWidth
                                placeholder="Phone number"
                              />
                            </TableCell>
                            <TableCell>
                              <Box>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={member.role}
                                    onChange={(e) => {
                                      const updatedTeam = [...formData.team];
                                      updatedTeam[index].role = e.target.value as CHSRole;
                                      // Reset customRole if not Community Member
                                      if (e.target.value !== 'Community Member') {
                                        updatedTeam[index].customRole = undefined;
                                      }
                                      setFormData(prev => ({ ...prev, team: updatedTeam }));
                                    }}
                                  >
                                    <MenuItem value="CHA">CHA</MenuItem>
                                    <MenuItem value="Facility In Charge">Facility In Charge</MenuItem>
                                    <MenuItem value="Health Facility Commitee Chair">Health Facility Committee Chair</MenuItem>
                                    <MenuItem value="CHP">CHP</MenuItem>
                                    <MenuItem value="CHC">CHC</MenuItem>
                                    <MenuItem value="Youth Member/ Adolescent">Youth Member/Adolescent</MenuItem>
                                    <MenuItem value="Community Member">Community Member</MenuItem>
                                  </Select>
                                </FormControl>
                                {member.role === 'Community Member' && (
                                  <TextField
                                    placeholder="Specify role"
                                    value={member.customRole || ''}
                                    onChange={(e) => {
                                      const updatedTeam = [...formData.team];
                                      updatedTeam[index].customRole = e.target.value;
                                      setFormData(prev => ({ ...prev, team: updatedTeam }));
                                    }}
                                    size="small"
                                    fullWidth
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <IconButton 
                                color="error"
                                disabled={isUpdatingTeam}
                                onClick={async () => {
                                  const updatedTeam = formData.team.filter((_, i) => i !== index);
                                  setFormData(prev => ({ ...prev, team: updatedTeam }));
                                  // Update the database with the new team
                                  await updateProjectTeamInDatabase(updatedTeam);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Box sx={{ p: 1 }}>
                      <Button
                        startIcon={<AddIcon />}
                        disabled={isUpdatingTeam}
                        onClick={() => {
                          const newTeamMember = { fullName: '', phone: '', role: 'CHA' as CHSRole };
                          const updatedTeam = [...formData.team, newTeamMember];
                          setFormData(prev => ({
                            ...prev,
                            team: updatedTeam
                          }));
                          // Don't update database immediately - wait for user to fill in the name
                          // Database will be updated when user types in the name field
                        }}
                      >
                        Add Team Member
                      </Button>
                    </Box>
                  </TableContainer>
                  </>
                )}
              </Box>
              
              {/* Change Plan Section */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>Change Plan</Typography>
                
                {isUpdatingActionPlan && (
                  <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                    Updating change plan in database...
                  </Alert>
                )}
                
                {/* Show info about loaded action plan */}
                {selectedProject && formData.actionPlan.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                    <Typography variant="body2">
                      Loaded {formData.actionPlan.length} change item(s) from the original project. You can add, edit, or remove items below.
                    </Typography>
                  </Alert>
                )}
                
                <TableContainer component={Paper}>
                  <Table size="small" sx={{
                    tableLayout: 'fixed',
                    minWidth: 1200,
                    '& .MuiTableCell-root': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      verticalAlign: 'top'
                    }
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Action</TableCell>
                        <TableCell>Responsible</TableCell>
                        <TableCell>Resources</TableCell>
                        <TableCell>By When</TableCell>
                        <TableCell>Place</TableCell>
                        <TableCell>MOV</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.actionPlan.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              value={item.action}
                              onChange={(e) => {
                                const updatedActionPlan = [...formData.actionPlan];
                                updatedActionPlan[index].action = e.target.value;
                                setFormData(prev => ({ ...prev, actionPlan: updatedActionPlan }));
                              }}
                              size="small"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.personResponsible}
                              onChange={(e) => {
                                const updatedActionPlan = [...formData.actionPlan];
                                updatedActionPlan[index].personResponsible = e.target.value;
                                setFormData(prev => ({ ...prev, actionPlan: updatedActionPlan }));
                              }}
                              size="small"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.resources}
                              onChange={(e) => {
                                const updatedActionPlan = [...formData.actionPlan];
                                updatedActionPlan[index].resources = e.target.value;
                                setFormData(prev => ({ ...prev, actionPlan: updatedActionPlan }));
                              }}
                              size="small"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.byWhen}
                              onChange={(e) => {
                                const updatedActionPlan = [...formData.actionPlan];
                                updatedActionPlan[index].byWhen = e.target.value;
                                setFormData(prev => ({ ...prev, actionPlan: updatedActionPlan }));
                              }}
                              size="small"
                              fullWidth
                              type="date"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.place}
                              onChange={(e) => {
                                const updatedActionPlan = [...formData.actionPlan];
                                updatedActionPlan[index].place = e.target.value;
                                setFormData(prev => ({ ...prev, actionPlan: updatedActionPlan }));
                              }}
                              size="small"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.mov}
                              onChange={(e) => {
                                const updatedActionPlan = [...formData.actionPlan];
                                updatedActionPlan[index].mov = e.target.value;
                                setFormData(prev => ({ ...prev, actionPlan: updatedActionPlan }));
                              }}
                              size="small"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={item.status}
                                onChange={(e) => {
                                  const updatedActionPlan = [...formData.actionPlan];
                                  updatedActionPlan[index].status = e.target.value as 'Not started' | 'In progress' | 'Completed';
                                  setFormData(prev => ({ ...prev, actionPlan: updatedActionPlan }));
                                }}
                              >
                                <MenuItem value="Not started">Not started</MenuItem>
                                <MenuItem value="In progress">In progress</MenuItem>
                                <MenuItem value="Completed">Completed</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              color="error"
                              disabled={isUpdatingActionPlan}
                              onClick={async () => {
                                const updatedActionPlan = formData.actionPlan.filter((_, i) => i !== index);
                                setFormData(prev => ({ ...prev, actionPlan: updatedActionPlan }));
                                // Update the database with the new action plan
                                await updateProjectActionPlanInDatabase(updatedActionPlan);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Box sx={{ p: 1 }}>
                    <Button
                      startIcon={<AddIcon />}
                      disabled={isUpdatingActionPlan}
                      onClick={async () => {
                        const newActionItem = {
                          action: '',
                          personResponsible: '',
                          resources: '',
                          byWhen: new Date().toISOString().split('T')[0], // Today's date as default
                          place: '',
                          mov: '',
                          status: 'Not started' as const
                        };
                        const updatedActionPlan = [...formData.actionPlan, newActionItem];
                        setFormData(prev => ({
                          ...prev,
                          actionPlan: updatedActionPlan
                        }));
                        // Update the database with the new action plan
                        await updateProjectActionPlanInDatabase(updatedActionPlan);
                      }}
                    >
                      Add Change Item
                    </Button>
                  </Box>
                </TableContainer>
              </Box>
              
              {/* Progress Visualization Section */}
              {selectedProject && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom>Progress Trend</Typography>
                  
                  <Box sx={{ height: 300, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                    {(() => {
                      // Start with project creation date at baseline percentage
                      const chartData: { date: string; percentage: number; numerator: number; denominator: number }[] = [];
                      
                      // Point 0: Project start date with baseline percentage
                      const rawStart = selectedProject?.firstCycleDate || selectedProject?.startDate || (selectedProject as any)?.createdAt;
                      if (rawStart) {
                        const parts = String(rawStart).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                        const startDate = parts
                          ? new Date(+parts[3], +parts[2] - 1, +parts[1])
                          : new Date(rawStart);
                        const baselineNum = parseFloat(selectedProject?.indicatorNumerator || '0');
                        const baselineDen = parseFloat(selectedProject?.indicatorDenominator || '0');
                        const baselinePct = baselineDen > 0 ? Math.round((baselineNum / baselineDen) * 1000) / 10 : 0;
                        if (!isNaN(startDate.getTime())) {
                          chartData.push({
                            date: startDate.toLocaleDateString(),
                            percentage: baselinePct,
                            numerator: baselineNum,
                            denominator: baselineDen,
                          });
                        }
                      }
                      
                      // Add all previous progress updates sorted by date
                      const sortedUpdates = [...previousUpdates].sort(
                        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                      );
                      
                      sortedUpdates.forEach(d => {
                        chartData.push({
                          date: new Date(d.createdAt).toLocaleDateString(),
                          percentage: d.denominator > 0 ? Math.round((d.numerator / d.denominator) * 1000) / 10 : 0,
                          numerator: d.numerator,
                          denominator: d.denominator,
                        });
                      });
                      
                      // Add current form entry as latest point if filled
                      if (formData.numerator > 0 && formData.denominator > 0) {
                        chartData.push({
                          date: new Date().toLocaleDateString(),
                          percentage: Math.round((formData.numerator / formData.denominator) * 1000) / 10,
                          numerator: formData.numerator,
                          denominator: formData.denominator,
                        });
                      }
                      
                      // Calculate if on track
                      const latestPct = chartData.length > 0 ? chartData[chartData.length - 1].percentage : 0;
                      const prevPct = chartData.length > 1 ? chartData[chartData.length - 2].percentage : 0;
                      const isOnTrack = latestPct >= prevPct;
                      
                      const target = selectedProject?.improvementTo;
                      
                      return (
                        <ProgressChart 
                          data={chartData}
                          isOnTrack={isOnTrack}
                          target={target}
                        />
                      );
                    })()}
                  </Box>
                </Box>
              )}
              
              {/* Best Sharing Forum Section - shown when project is ongoing, past_completion, or at completion */}
              {selectedProject && (selectedProject.status === 'ongoing' || selectedProject.status === 'past_completion' || shouldMarkProjectAsCompleted(formData.numerator, formData.denominator)) && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom>Best Practice Sharing</Typography>
                  
                  <FormControl component="fieldset">
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Has the project been shared in the best practice sharing forum?
                    </Typography>
                    <RadioGroup
                      value={formData.sharedInForum ? 'yes' : 'no'}
                      onChange={async (e) => {
                        const isShared = e.target.value === 'yes';
                        setFormData(prev => ({ ...prev, sharedInForum: isShared }));
                        
                        // If user selects "Yes", immediately update project status to archived
                        if (isShared) {
                          await updateProjectStatusToArchived();
                        }
                      }}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={!selectedProjectId}
          >
            Add Progress Update
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateProgress;
